import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './Equipment.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SUPABASE_URL = 'https://yulypcgdgxzqtdkysywg.supabase.co';

const EQUIPMENT_TYPES = ['Vehicle', 'Lifting Equipment', 'Power Tool', 'Hand Tool', 'Electrical Equipment', 'Measuring Equipment', 'PPE', 'Other'];

const INSPECTION_TYPES = ['LOLER', 'PAT Test', 'Calibration', 'Pre-use Check', 'Service', 'MOT', 'Other'];

const STATUS_CONFIG = {
  'active':       { label: 'Active',       cls: 'eq-status-active'    },
  'in service':   { label: 'In Service',   cls: 'eq-status-active'    },
  'out of service': { label: 'Out of Service', cls: 'eq-status-out'   },
  'retired':      { label: 'Retired',      cls: 'eq-status-retired'   },
};

const PRE_USE_ITEMS = [
  { id: 'tyres',      label: 'Tyres', detail: 'Condition and pressure OK, no damage or excessive wear' },
  { id: 'lights',     label: 'Lights', detail: 'All lights working — headlights, indicators, brake lights, hazards' },
  { id: 'brakes',     label: 'Brakes', detail: 'Brakes feel normal, no pulling or unusual noise' },
  { id: 'fluids',     label: 'Fluid Levels', detail: 'Oil, coolant and screenwash levels satisfactory' },
  { id: 'windscreen', label: 'Windscreen', detail: 'Clean, no cracks or damage affecting visibility' },
  { id: 'mirrors',    label: 'Mirrors', detail: 'Clean and correctly adjusted' },
  { id: 'horn',       label: 'Horn', detail: 'Working correctly' },
  { id: 'load',       label: 'Load Security', detail: 'Any equipment or load properly secured' },
  { id: 'dashboard',  label: 'Dashboard', detail: 'No warning lights illuminated' },
  { id: 'exterior',   label: 'Exterior', detail: 'No new damage to bodywork or windows' },
];
const EMPTY_FORM = {
  name: '', type: 'Lifting Equipment', make: '', model: '',
  serial_number: '', registration: '', status: 'active',
  purchase_date: '', assigned_to: '',
  requires_loler: false, requires_calibration: false, requires_pat: false,
  mot_expiry: '', tax_expiry: '',
  notes: '', photo_url: '',
};

const EMPTY_INSPECTION = {
  inspection_type: 'LOLER', inspection_date: '', next_due_date: '',
  result: 'pass', external_provider: '', inspected_by: '',
  reminder_days: 30, notes: '', certificate_url: '',
};

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(str) {
  if (!str) return null;
  return Math.ceil((new Date(str) - new Date()) / 86400000);
}

function getDueStatus(date) {
  if (!date) return null;
  const days = daysUntil(date);
  if (days < 0) return 'overdue';
  if (days <= 30) return 'due-soon';
  return 'current';
}

function DueBadge({ date, label }) {
  const status = getDueStatus(date);
  if (!date) return <span className="eq-due-badge missing">{label}: Not scheduled</span>;
  const days = daysUntil(date);
  return (
    <span className={`eq-due-badge ${status}`}>
      {label}: {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : days <= 30 ? `${days}d` : formatDate(date)}
    </span>
  );
}

export default function Equipment({ user }) {
  const [equipment, setEquipment] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [archivedInspections, setArchivedInspections] = useState([]);
  const [showArchivedInsp, setShowArchivedInsp] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'add' | 'detail' | 'edit' | 'add-inspection'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [inspForm, setInspForm] = useState(EMPTY_INSPECTION);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [uploadingCert, setUploadingCert] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedInsp, setSelectedInsp] = useState(null);
  const photoInputRef = React.useRef();

  // Pre-use check state
  const [preUseChecks, setPreUseChecks] = useState({});
  const [preUseNotes, setPreUseNotes] = useState('');
  const [savingPreUse, setSavingPreUse] = useState(false);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const ext = file.name.split('.').pop();
      const safeName = (form.name || 'equipment').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const path = `documents/equipment/${safeName}_${Date.now()}.${ext}`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/vera-documents/${path}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_KEY || ''}`,
          'Content-Type': file.type,
          'x-upsert': 'true',
        },
        body: file,
      });
      if (res.ok) {
        const url = `${SUPABASE_URL}/storage/v1/object/public/vera-documents/${path}`;
        setForm(prev => ({ ...prev, photo_url: url }));
      }
    } catch (err) { console.error('Photo upload error:', err); }
    finally { setUploadingPhoto(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [eqRes, inspRes, archivedInspRes, userRes] = await Promise.all([
        axios.get(`${API}/equipment`),
        axios.get(`${API}/inspections`),
        axios.get(`${API}/inspections?archived=true`),
        axios.get(`${API}/users/all`),
      ]);
      setEquipment(eqRes.data);
      setInspections(inspRes.data);
      setArchivedInspections(archivedInspRes.data || []);
      setUsers(userRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const equipmentInspections = (eqId) => inspections.filter(i => i.equipment_id === eqId);

  const latestInspection = (eqId, type) => {
    const matching = inspections
      .filter(i => i.equipment_id === eqId && i.inspection_type === type)
      .sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date));
    return matching[0] || null;
  };

  const counts = useMemo(() => ({
    total:    equipment.filter(e => !e.is_archived).length,
    active:   equipment.filter(e => !e.is_archived && (e.status === 'active' || e.status === 'in service')).length,
    overdue:  equipment.filter(e => {
      if (e.is_archived) return false;
      const eqInsp = inspections.filter(i => i.equipment_id === e.id);
      return eqInsp.some(i => i.next_due_date && daysUntil(i.next_due_date) < 0);
    }).length,
    dueSoon: equipment.filter(e => {
      if (e.is_archived) return false;
      const eqInsp = inspections.filter(i => i.equipment_id === e.id);
      return eqInsp.some(i => i.next_due_date && daysUntil(i.next_due_date) >= 0 && daysUntil(i.next_due_date) <= 30);
    }).length,
  }), [equipment, inspections]);

  const filtered = useMemo(() => {
    return equipment
      .filter(e => !e.is_archived)
      .filter(e => typeFilter === 'All' || e.type === typeFilter)
      .filter(e => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (e.name || '').toLowerCase().includes(q) ||
               (e.make || '').toLowerCase().includes(q) ||
               (e.model || '').toLowerCase().includes(q) ||
               (e.serial_number || '').toLowerCase().includes(q) ||
               (e.registration || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [equipment, typeFilter, search]);

  const userName = (id) => {
    const u = users.find(u => u.id === id);
    return u ? `${u.first_name} ${u.last_name}` : '—';
  };

  const handleSave = async () => {
    if (!form.name) { alert('Name is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        assigned_to: form.assigned_to || null,
        purchase_date: form.purchase_date || null,
        requires_loler: form.requires_loler === true || form.requires_loler === 'true',
        requires_calibration: form.requires_calibration === true || form.requires_calibration === 'true',
        requires_pat: form.requires_pat === true || form.requires_pat === 'true',
        created_by: user.id,
      };
      if (selected && view === 'edit') {
        await axios.patch(`${API}/equipment/${selected.id}`, { ...payload, updated_by: user.id });
      } else {
        await axios.post(`${API}/equipment`, payload);
      }
      setView('list');
      setForm(EMPTY_FORM);
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error saving: ' + err.message); }
    finally { setSaving(false); }
  };

  const [analysing, setAnalysing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inspFileRef = React.useRef();

  const toBase64 = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(',')[1]);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const analyseInspectionDoc = async (file) => {
    setAnalysing(true);
    setAnalysisStatus('Reading certificate...');
    try {
      const base64 = await toBase64(file);
      const response = await axios.post(`${API}/analyse/inspection`, {
        base64,
        mediaType: file.type,
        equipmentName: selected?.name || '',
      });
      const data = response.data;
      setInspForm(prev => ({
        ...prev,
        inspection_type: data.inspection_type || prev.inspection_type,
        inspection_date: data.inspection_date || prev.inspection_date,
        next_due_date: data.next_due_date || prev.next_due_date,
        result: data.result || prev.result,
        external_provider: data.external_provider || prev.external_provider,
        notes: data.notes || prev.notes,
        reminder_days: data.reminder_days || prev.reminder_days,
      }));
      setAnalysisStatus('Done! Check the fields below.');
      setTimeout(() => setAnalysisStatus(''), 3000);
    } catch (err) {
      setAnalysisStatus('Could not analyse — please fill in manually.');
      setTimeout(() => setAnalysisStatus(''), 3000);
    } finally {
      setAnalysing(false);
    }
  };

  const handleInspFileDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    // First upload, then analyse
    await uploadAndAnalyse(file);
  };

  const uploadAndAnalyse = async (file) => {
    setUploadingCert(true);
    try {
      const ext = file.name.split('.').pop();
      const safeName = (selected?.name || inspForm.inspection_type || 'inspection').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const path = `documents/inspections/${safeName}_${Date.now()}.${ext}`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/vera-documents/${path}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_KEY || ''}`,
          'Content-Type': file.type,
          'x-upsert': 'true',
        },
        body: file,
      });
      if (res.ok) {
        const url = `${SUPABASE_URL}/storage/v1/object/public/vera-documents/${path}`;
        setInspForm(prev => ({ ...prev, certificate_url: url }));
      }
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploadingCert(false); }
    // Then analyse
    if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
      await analyseInspectionDoc(file);
    }
  };

  const uploadCert = async (file) => {
    if (!file) return;
    await uploadAndAnalyse(file);
  };

  const handleSaveInspection = async () => {
    if (!inspForm.inspection_date) { alert('Inspection date is required.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/inspections`, {
        ...inspForm,
        equipment_id: selected.id,
        inspected_by: inspForm.inspected_by || null,
        next_due_date: inspForm.next_due_date || null,
        reminder_days: parseInt(inspForm.reminder_days) || 30,
        created_by: user.id,
      });
      setView('detail');
      setInspForm(EMPTY_INSPECTION);
      fetchAll();
    } catch (err) { alert('Error saving inspection: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleSavePreUse = async () => {
    const allAnswered = PRE_USE_ITEMS.every(item => preUseChecks[item.id] !== undefined);
    if (!allAnswered) { alert('Please check all items before submitting.'); return; }
    setSavingPreUse(true);
    try {
      const failedItems = PRE_USE_ITEMS.filter(item => preUseChecks[item.id] === false);
      const result = failedItems.length === 0 ? 'pass' : 'fail';
      const notes = [
        failedItems.length > 0 ? `DEFECTS: ${failedItems.map(i => i.label).join(', ')}` : '',
        preUseNotes ? `Notes: ${preUseNotes}` : '',
      ].filter(Boolean).join(' | ');

      // Save inspection record
      await axios.post(`${API}/inspections`, {
        equipment_id: selected.id,
        inspection_type: 'Pre-use Check',
        inspection_date: new Date().toISOString().slice(0, 10),
        result,
        inspected_by: user.id,
        notes: notes || null,
        next_due_date: null,
        reminder_days: 1,
        created_by: user.id,
      });

      // Auto-raise NCR if any failures
      if (failedItems.length > 0) {
        await axios.post(`${API}/non-conformances`, {
          title: `Pre-use check defect — ${selected.name} (${selected.registration || ''})`,
          description: `Pre-use vehicle inspection identified the following defects: ${failedItems.map(i => `${i.label} — ${i.detail}`).join('. ')}${preUseNotes ? `. Driver notes: ${preUseNotes}` : ''}`,
          severity: 'minor',
          source: 'Internal',
          status: 'open',
          notes: `Auto-raised from pre-use check on ${new Date().toLocaleDateString('en-GB')} by ${user.first_name} ${user.last_name}`,
          created_by: user.id,
        });
        alert(`Pre-use check saved. ${failedItems.length} defect(s) found — an NCR has been raised automatically.`);
      } else {
        alert('Pre-use check completed — all items passed.');
      }

      setView('detail');
      setPreUseChecks({});
      setPreUseNotes('');
      fetchAll();
    } catch (err) { alert('Error saving: ' + err.message); }
    finally { setSavingPreUse(false); }
  };

  const handleDelete = async (eq) => {
    if (!window.confirm(`Permanently delete ${eq.name}?`)) return;
    try {
      await axios.delete(`${API}/equipment/${eq.id}`);
      setView('list');
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error deleting.'); }
  };

  const openEdit = (eq) => {
    setSelected(eq);
    setForm({
      name: eq.name || '', type: eq.type || 'Lifting Equipment',
      make: eq.make || '', model: eq.model || '',
      serial_number: eq.serial_number || '', registration: eq.registration || '',
      status: eq.status || 'active', purchase_date: eq.purchase_date || '',
      assigned_to: eq.assigned_to || '',
      requires_loler: eq.requires_loler || false,
      requires_calibration: eq.requires_calibration || false,
      requires_pat: eq.requires_pat || false,
      mot_expiry: eq.mot_expiry || '',
      tax_expiry: eq.tax_expiry || '',
      notes: eq.notes || '',
      photo_url: eq.photo_url || '',
    });
    setView('edit');
  };

  if (loading) return <div className="eq-loading">Loading equipment...</div>;

  // ── PRE-USE CHECK ────────────────────────────────────────────
  if (view === 'pre-use' && selected) {
    const allAnswered = PRE_USE_ITEMS.every(item => preUseChecks[item.id] !== undefined);
    const anyFailed = PRE_USE_ITEMS.some(item => preUseChecks[item.id] === false);
    return (
      <div className="eq-preuse-page">
        <button className="eq-back" onClick={() => { setView('detail'); setPreUseChecks({}); setPreUseNotes(''); }}>← {selected.name.toUpperCase()}</button>
        <div className="eq-preuse-header">
          <h2>PRE-USE CHECK</h2>
          <div className="eq-preuse-vehicle">
            {selected.name}
            {selected.registration && <span className="eq-reg-badge">{selected.registration}</span>}
          </div>
          <div className="eq-preuse-date">{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</div>
          <div className="eq-preuse-driver">Driver: {user.first_name} {user.last_name}</div>
        </div>

        <div className="eq-preuse-checklist">
          {PRE_USE_ITEMS.map(item => {
            const val = preUseChecks[item.id];
            return (
              <div key={item.id} className={`eq-preuse-item ${val === true ? 'pass' : val === false ? 'fail' : ''}`}>
                <div className="eq-preuse-item-info">
                  <div className="eq-preuse-item-label">{item.label}</div>
                  <div className="eq-preuse-item-detail">{item.detail}</div>
                </div>
                <div className="eq-preuse-item-buttons">
                  <button
                    className={`eq-preuse-btn-ok ${val === true ? 'selected' : ''}`}
                    onClick={() => setPreUseChecks(prev => ({ ...prev, [item.id]: true }))}
                  >✓</button>
                  <button
                    className={`eq-preuse-btn-fail ${val === false ? 'selected' : ''}`}
                    onClick={() => setPreUseChecks(prev => ({ ...prev, [item.id]: false }))}
                  >✕</button>
                </div>
              </div>
            );
          })}
        </div>

        {anyFailed && (
          <div className="eq-preuse-notes">
            <label>DEFECT DETAILS (describe any issues found)</label>
            <textarea
              rows={3}
              value={preUseNotes}
              onChange={e => setPreUseNotes(e.target.value)}
              placeholder="Describe the defects found..."
            />
          </div>
        )}

        <div className="eq-preuse-progress">
          {PRE_USE_ITEMS.filter(i => preUseChecks[i.id] !== undefined).length} / {PRE_USE_ITEMS.length} checked
        </div>

        <button
          className={`eq-preuse-submit ${!allAnswered || savingPreUse ? 'disabled' : anyFailed ? 'has-fails' : 'all-pass'}`}
          onClick={handleSavePreUse}
          disabled={!allAnswered || savingPreUse}
        >
          {savingPreUse ? 'SAVING...' : !allAnswered ? 'CHECK ALL ITEMS TO SUBMIT' : anyFailed ? '⚠ SUBMIT — DEFECTS FOUND' : '✓ SUBMIT — ALL PASSED'}
        </button>
      </div>
    );
  }

  // ── ADD INSPECTION ───────────────────────────────────────────
  if (view === 'add-inspection' && selected) return (
    <div className="eq-form-page">
      <button className="eq-back" onClick={() => { setView('detail'); setInspForm(EMPTY_INSPECTION); setAnalysisStatus(''); }}>← {selected.name.toUpperCase()}</button>
      <div className="eq-form-header">
        <h2>ADD INSPECTION RECORD</h2>
        <p>{selected.name} — Drop a certificate and Claude will fill in the details automatically</p>
      </div>

      {/* Smart drop zone */}
      <div
        className={`eq-drop-zone ${dragOver ? 'drag-over' : ''} ${inspForm.certificate_url ? 'has-file' : ''}`}
        onDrop={handleInspFileDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => inspFileRef.current?.click()}
      >
        {analysing || uploadingCert ? (
          <div className="eq-drop-analysing">
            <div className="eq-drop-spinner" />
            <span>{analysing ? analysisStatus : 'Uploading...'}</span>
          </div>
        ) : inspForm.certificate_url ? (
          <div className="eq-drop-done">
            <span className="eq-drop-tick">✓</span>
            <span>Certificate uploaded{analysisStatus ? ` — ${analysisStatus}` : ' — fields pre-filled below'}</span>
            <button className="eq-drop-change" onClick={e => { e.stopPropagation(); inspFileRef.current?.click(); }}>CHANGE</button>
          </div>
        ) : (
          <div className="eq-drop-empty">
            <div className="eq-drop-icon">⬆</div>
            <div className="eq-drop-text">Drop your certificate here or click to browse</div>
            <div className="eq-drop-hint">PDF or image — Claude will read and fill in the fields</div>
          </div>
        )}
        <input ref={inspFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => uploadAndAnalyse(e.target.files[0])} />
      </div>

      <div className="eq-form">
        <div className="eq-field">
          <label>INSPECTION TYPE</label>
          <select value={inspForm.inspection_type} onChange={e => setInspForm({ ...inspForm, inspection_type: e.target.value })}>
            {INSPECTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="eq-field">
          <label>RESULT</label>
          <select value={inspForm.result} onChange={e => setInspForm({ ...inspForm, result: e.target.value })}>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
            <option value="advisory">Advisory</option>
            <option value="conditional">Conditional Pass</option>
          </select>
        </div>
        <div className="eq-field">
          <label>INSPECTION DATE *</label>
          <input type="date" value={inspForm.inspection_date} onChange={e => setInspForm({ ...inspForm, inspection_date: e.target.value })} />
        </div>
        <div className="eq-field">
          <label>NEXT DUE DATE</label>
          <input type="date" value={inspForm.next_due_date} onChange={e => setInspForm({ ...inspForm, next_due_date: e.target.value })} />
        </div>
        <div className="eq-field">
          <label>INSPECTED BY (INTERNAL)</label>
          <select value={inspForm.inspected_by} onChange={e => setInspForm({ ...inspForm, inspected_by: e.target.value })}>
            <option value="">— Select —</option>
            {users.filter(u => !u.is_archived).map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
          </select>
        </div>
        <div className="eq-field">
          <label>EXTERNAL PROVIDER</label>
          <input value={inspForm.external_provider} onChange={e => setInspForm({ ...inspForm, external_provider: e.target.value })} placeholder="e.g. Bureau Veritas, SGS" />
        </div>
        <div className="eq-field">
          <label>ALERT (DAYS BEFORE DUE)</label>
          <input type="number" value={inspForm.reminder_days} onChange={e => setInspForm({ ...inspForm, reminder_days: e.target.value })} />
        </div>
        <div className="eq-field">
          <label>CERTIFICATE</label>
          <div className="eq-file-row">
            {inspForm.certificate_url && <a href={inspForm.certificate_url} target="_blank" rel="noreferrer" className="eq-view-link">View ↗</a>}
            <label className="eq-upload-btn">
              {uploadingCert ? 'Uploading...' : inspForm.certificate_url ? 'Replace' : 'Upload'}
              <input type="file" accept=".pdf,.jpg,.png" style={{ display: 'none' }} onChange={e => uploadCert(e.target.files[0])} />
            </label>
          </div>
        </div>
        <div className="eq-field full">
          <label>NOTES</label>
          <textarea rows={3} value={inspForm.notes} onChange={e => setInspForm({ ...inspForm, notes: e.target.value })} placeholder="Any observations or notes..." />
        </div>
        <div className="eq-form-actions">
          <button className="eq-btn secondary" onClick={() => { setView('detail'); setInspForm(EMPTY_INSPECTION); }}>CANCEL</button>
          <button className={`eq-btn primary ${saving ? 'disabled' : ''}`} onClick={handleSaveInspection} disabled={saving}>
            {saving ? 'SAVING...' : 'SAVE INSPECTION'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── ADD / EDIT EQUIPMENT ─────────────────────────────────────
  if (view === 'add' || view === 'edit') return (
    <div className="eq-form-page">
      <button className="eq-back" onClick={() => { setView(selected ? 'detail' : 'list'); }}>
        ← {selected ? selected.name.toUpperCase() : 'EQUIPMENT'}
      </button>
      <div className="eq-form-header">
        <h2>{view === 'edit' ? 'EDIT EQUIPMENT' : 'ADD EQUIPMENT'}</h2>
      </div>
      <div className="eq-form">
        <div className="eq-field full">
          <label>NAME *</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Transit Van — LU21 ABC" />
        </div>
        <div className="eq-field">
          <label>TYPE</label>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="eq-field">
          <label>STATUS</label>
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            <option value="active">Active</option>
            <option value="in service">In Service</option>
            <option value="out of service">Out of Service</option>
            <option value="retired">Retired</option>
          </select>
        </div>
        <div className="eq-field">
          <label>MAKE</label>
          <input value={form.make} onChange={e => setForm({ ...form, make: e.target.value })} placeholder="e.g. Ford" />
        </div>
        <div className="eq-field">
          <label>MODEL</label>
          <input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} placeholder="e.g. Transit" />
        </div>
        <div className="eq-field">
          <label>SERIAL NUMBER</label>
          <input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} />
        </div>
        <div className="eq-field">
          <label>REGISTRATION</label>
          <input value={form.registration} onChange={e => setForm({ ...form, registration: e.target.value })} placeholder="e.g. LU21 ABC" />
        </div>
        <div className="eq-field">
          <label>PURCHASE DATE</label>
          <input type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} />
        </div>
        <div className="eq-field">
          <label>ASSIGNED TO</label>
          <select value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
            <option value="">— Not assigned —</option>
            {users.filter(u => !u.is_archived).map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
          </select>
        </div>

        <div className="eq-field-group-title">INSPECTION REQUIREMENTS</div>
        <div className="eq-field">
          <label>REQUIRES LOLER INSPECTION?</label>
          <select value={form.requires_loler} onChange={e => setForm({ ...form, requires_loler: e.target.value === 'true' })}>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
        <div className="eq-field">
          <label>REQUIRES PAT TEST?</label>
          <select value={form.requires_pat} onChange={e => setForm({ ...form, requires_pat: e.target.value === 'true' })}>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
        <div className="eq-field">
          <label>REQUIRES CALIBRATION?</label>
          <select value={form.requires_calibration} onChange={e => setForm({ ...form, requires_calibration: e.target.value === 'true' })}>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
        <div className="eq-field-group-title">VEHICLE (if applicable)</div>
        <div className="eq-field">
          <label>MOT EXPIRY DATE</label>
          <input type="date" value={form.mot_expiry} onChange={e => setForm({ ...form, mot_expiry: e.target.value })} />
        </div>
        <div className="eq-field">
          <label>ROAD TAX EXPIRY DATE</label>
          <input type="date" value={form.tax_expiry} onChange={e => setForm({ ...form, tax_expiry: e.target.value })} />
        </div>

        <div className="eq-field full">
          <label>NOTES</label>
          <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="eq-field full">
          <label>EQUIPMENT PHOTO</label>
          <div className="eq-photo-upload">
            {form.photo_url ? (
              <div className="eq-photo-preview">
                <img src={form.photo_url} alt="Equipment" className="eq-photo-img" />
                <div className="eq-photo-actions">
                  <label className="eq-upload-btn">
                    {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                    <input type="file" accept=".jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={e => uploadPhoto(e.target.files[0])} />
                  </label>
                  <button className="eq-upload-btn" onClick={() => setForm({ ...form, photo_url: '' })}>Remove</button>
                </div>
              </div>
            ) : (
              <label className="eq-photo-dropzone">
                {uploadingPhoto ? 'Uploading...' : (
                  <>
                    <span className="eq-photo-icon">📷</span>
                    <span>Click to upload a photo</span>
                    <span className="eq-photo-hint">JPG or PNG</span>
                  </>
                )}
                <input type="file" accept=".jpg,.jpeg,.png,.webp" style={{ display: 'none' }} onChange={e => uploadPhoto(e.target.files[0])} />
              </label>
            )}
          </div>
        </div>
        <div className="eq-form-actions">
          <button className="eq-btn secondary" onClick={() => { setView(selected ? 'detail' : 'list'); }}>CANCEL</button>
          <button className={`eq-btn primary ${saving ? 'disabled' : ''}`} onClick={handleSave} disabled={saving}>
            {saving ? 'SAVING...' : view === 'edit' ? 'SAVE CHANGES' : 'ADD EQUIPMENT'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── DETAIL VIEW ──────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const eqInspections = equipmentInspections(selected.id).sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date));
    const lolerInsp = latestInspection(selected.id, 'LOLER');
    const patInsp = latestInspection(selected.id, 'PAT Test');
    const calInsp = latestInspection(selected.id, 'Calibration');
    const motInsp = latestInspection(selected.id, 'MOT');
    const status = STATUS_CONFIG[selected.status] || STATUS_CONFIG['active'];

    return (
      <div className="eq-detail-page">
        <button className="eq-back" onClick={() => { setView('list'); setSelected(null); }}>← EQUIPMENT</button>
        <div className="eq-detail-header">
          <div>
            <h2 className="eq-detail-name">{selected.name}</h2>
            <div className="eq-detail-meta">
              <span className="eq-type-badge">{selected.type}</span>
              <span className={`eq-status-badge ${status.cls}`}>{status.label}</span>
              {selected.make && <span className="eq-detail-make">{selected.make} {selected.model}</span>}
              {selected.registration && <span className="eq-reg-badge">{selected.registration}</span>}
            </div>
          </div>
          {user.role === 'admin' && (
            <div className="eq-detail-actions">
              <button className="eq-btn primary" onClick={() => { setInspForm(EMPTY_INSPECTION); setView('add-inspection'); }}>+ ADD INSPECTION</button>
              <button className="eq-btn secondary" onClick={() => openEdit(selected)}>EDIT</button>
              <button className="eq-btn danger" onClick={() => handleDelete(selected)}>DELETE</button>
            </div>
          )}
          {selected.type === 'Vehicle' && (
            <button className="eq-preuse-btn" onClick={() => { setPreUseChecks({}); setPreUseNotes(''); setView('pre-use'); }}>
              ✓ PRE-USE CHECK
            </button>
          )}
        </div>

        <div className="eq-detail-grid">
          <div className="eq-detail-col">
            {selected.photo_url && (
              <div className="eq-detail-photo">
                <img src={selected.photo_url} alt={selected.name} onClick={() => window.open(selected.photo_url, '_blank')} style={{ cursor: 'zoom-in' }} />
                {user.role === 'admin' && (
                  <button className="eq-photo-delete-btn" onClick={async () => {
                    if (!window.confirm('Remove this photo? It will be deleted from storage.')) return;
                    await axios.delete(`${API}/equipment/${selected.id}/photo`);
                    setSelected({ ...selected, photo_url: null });
                    fetchAll();
                  }}>✕ Remove photo</button>
                )}
              </div>
            )}
            <div className="eq-detail-block">
              <div className="eq-block-title">DETAILS</div>
              {selected.serial_number && <div className="eq-block-row"><span>Serial No.</span><span>{selected.serial_number}</span></div>}
              {selected.purchase_date && <div className="eq-block-row"><span>Purchased</span><span>{formatDate(selected.purchase_date)}</span></div>}
              {selected.assigned_to && <div className="eq-block-row"><span>Assigned to</span><span>{userName(selected.assigned_to)}</span></div>}
              {selected.notes && <div className="eq-block-row"><span>Notes</span><span>{selected.notes}</span></div>}
            </div>

            <div className="eq-detail-block">
              <div className="eq-block-title">INSPECTION STATUS</div>
              {selected.requires_loler && (
                <div className="eq-insp-status-row">
                  <span className="eq-insp-type">LOLER</span>
                  <span className={`eq-insp-due ${getDueStatus(lolerInsp?.next_due_date)}`}>
                    {lolerInsp ? `Due: ${formatDate(lolerInsp.next_due_date)}` : 'No record'}
                  </span>
                  {lolerInsp?.certificate_url && <a href={lolerInsp.certificate_url} target="_blank" rel="noreferrer" className="eq-view-link">Cert ↗</a>}
                </div>
              )}
              {selected.requires_pat && (
                <div className="eq-insp-status-row">
                  <span className="eq-insp-type">PAT Test</span>
                  <span className={`eq-insp-due ${getDueStatus(patInsp?.next_due_date)}`}>
                    {patInsp ? `Due: ${formatDate(patInsp.next_due_date)}` : 'No record'}
                  </span>
                  {patInsp?.certificate_url && <a href={patInsp.certificate_url} target="_blank" rel="noreferrer" className="eq-view-link">Cert ↗</a>}
                </div>
              )}
              {selected.requires_calibration && (
                <div className="eq-insp-status-row">
                  <span className="eq-insp-type">Calibration</span>
                  <span className={`eq-insp-due ${getDueStatus(calInsp?.next_due_date)}`}>
                    {calInsp ? `Due: ${formatDate(calInsp.next_due_date)}` : 'No record'}
                  </span>
                  {calInsp?.certificate_url && <a href={calInsp.certificate_url} target="_blank" rel="noreferrer" className="eq-view-link">Cert ↗</a>}
                </div>
              )}
              {motInsp && (
                <div className="eq-insp-status-row">
                  <span className="eq-insp-type">MOT</span>
                  <span className={`eq-insp-due ${getDueStatus(motInsp?.next_due_date)}`}>
                    {motInsp ? `Due: ${formatDate(motInsp.next_due_date)}` : 'No record'}
                  </span>
                  {motInsp?.certificate_url && <a href={motInsp.certificate_url} target="_blank" rel="noreferrer" className="eq-view-link">Cert ↗</a>}
                </div>
              )}
              {!selected.requires_loler && !selected.requires_pat && !selected.requires_calibration && eqInspections.length === 0 && !selected.mot_expiry && !selected.tax_expiry && (
                <p className="eq-no-insp">No inspection requirements set.</p>
              )}
              {selected.mot_expiry && (
                <div className="eq-insp-status-row">
                  <span className="eq-insp-type">MOT</span>
                  <span className={`eq-insp-due ${getDueStatus(selected.mot_expiry)}`}>Due: {formatDate(selected.mot_expiry)}</span>
                </div>
              )}
              {selected.tax_expiry && (
                <div className="eq-insp-status-row">
                  <span className="eq-insp-type">Road Tax</span>
                  <span className={`eq-insp-due ${getDueStatus(selected.tax_expiry)}`}>Due: {formatDate(selected.tax_expiry)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="eq-detail-col">
            <div className="eq-detail-block">
              <div className="eq-block-title-row">
                <span className="eq-block-title">INSPECTION HISTORY ({eqInspections.length})</span>
                {archivedInspections.filter(i => i.equipment_id === selected.id).length > 0 && (
                  <button className="eq-show-archived-btn" onClick={() => setShowArchivedInsp(!showArchivedInsp)}>
                    {showArchivedInsp ? 'HIDE ARCHIVED' : `SHOW ARCHIVED (${archivedInspections.filter(i => i.equipment_id === selected.id).length})`}
                  </button>
                )}
              </div>
              {eqInspections.length === 0 ? (
                <p className="eq-no-insp">No inspections recorded yet.</p>
              ) : (
                eqInspections.map(insp => (
                  <div key={insp.id} className="eq-insp-row clickable" onClick={() => setSelectedInsp(insp)}>
                    <div className="eq-insp-row-top">
                      <span className="eq-insp-type-tag">{insp.inspection_type}</span>
                      <span className={`eq-insp-result ${insp.result}`}>{insp.result?.toUpperCase()}</span>
                      <span className="eq-insp-date">{formatDate(insp.inspection_date)}</span>
                      {insp.certificate_url && <span className="eq-insp-has-cert">📄</span>}
                    </div>
                    <div className="eq-insp-row-meta">
                      {insp.external_provider && <span>{insp.external_provider}</span>}
                      {insp.next_due_date && <span className={`eq-insp-due ${getDueStatus(insp.next_due_date)}`}>Next: {formatDate(insp.next_due_date)}</span>}
                    </div>
                    {insp.notes && <div className="eq-insp-notes">{insp.notes}</div>}
                  </div>
                ))
              )}
              {showArchivedInsp && archivedInspections.filter(i => i.equipment_id === selected.id).map(insp => (
                <div key={insp.id} className="eq-insp-row clickable archived" onClick={() => setSelectedInsp(insp)}>
                  <div className="eq-insp-row-top">
                    <span className="eq-insp-type-tag">{insp.inspection_type}</span>
                    <span className={`eq-insp-result ${insp.result}`}>{insp.result?.toUpperCase()}</span>
                    <span className="eq-insp-date">{formatDate(insp.inspection_date)}</span>
                    <span className="eq-archived-badge">ARCHIVED</span>
                    {insp.certificate_url && <span className="eq-insp-has-cert">📄</span>}
                  </div>
                  <div className="eq-insp-row-meta">
                    {insp.external_provider && <span>{insp.external_provider}</span>}
                    {insp.next_due_date && <span className="eq-insp-due overdue">Next: {formatDate(insp.next_due_date)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      {/* Inspection detail modal */}
      {selectedInsp && (
        <div className="eq-modal-overlay" onClick={() => setSelectedInsp(null)}>
          <div className="eq-modal" onClick={e => e.stopPropagation()}>
            <button className="eq-modal-close" onClick={() => setSelectedInsp(null)}>✕</button>
            <div className="eq-modal-header">
              <span className={`eq-insp-result ${selectedInsp.result}`}>{selectedInsp.result?.toUpperCase()}</span>
              <h3>{selectedInsp.inspection_type} Inspection</h3>
              <p>{formatDate(selectedInsp.inspection_date)}</p>
            </div>
            <div className="eq-modal-body">
              {selectedInsp.external_provider && <div className="eq-modal-row"><span>Provider</span><span>{selectedInsp.external_provider}</span></div>}
              {selectedInsp.inspected_by && <div className="eq-modal-row"><span>Inspected by</span><span>{userName(selectedInsp.inspected_by)}</span></div>}
              {selectedInsp.next_due_date && <div className="eq-modal-row"><span>Next due</span><span className={`eq-insp-due ${getDueStatus(selectedInsp.next_due_date)}`}>{formatDate(selectedInsp.next_due_date)}</span></div>}
              {selectedInsp.notes && (
                <div className="eq-modal-notes">
                  <div className="eq-modal-notes-label">OBSERVATIONS / DEFECTS</div>
                  <p>{selectedInsp.notes}</p>
                </div>
              )}
            </div>
            {selectedInsp.certificate_url && (
              <div className="eq-modal-footer">
                <a href={selectedInsp.certificate_url} target="_blank" rel="noreferrer" className="eq-btn primary">VIEW CERTIFICATE ↗</a>
              </div>
            )}
            {user.role === 'admin' && (
              <div className="eq-modal-admin">
                <button className="eq-btn secondary" onClick={async () => {
                  if (!window.confirm('Archive this inspection? It will be hidden but not deleted.')) return;
                  await axios.patch(`${API}/inspections/${selectedInsp.id}`, { is_archived: true, archived_at: new Date().toISOString(), archived_by: user.id });
                  setSelectedInsp(null); fetchAll();
                }}>ARCHIVE</button>
                <button className="eq-btn danger" onClick={async () => {
                  if (!window.confirm('Permanently delete this inspection record?')) return;
                  await axios.delete(`${API}/inspections/${selectedInsp.id}`);
                  setSelectedInsp(null); fetchAll();
                }}>DELETE</button>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    );
  }
  return (
    <div className="eq-page">
      {/* Stat cards */}
      <div className="eq-stats">
        <div className="eq-stat"><span className="eq-stat-value">{counts.total}</span><span className="eq-stat-label">Total</span></div>
        <div className="eq-stat active"><span className="eq-stat-value">{counts.active}</span><span className="eq-stat-label">Active</span></div>
        <div className="eq-stat overdue"><span className="eq-stat-value">{counts.overdue}</span><span className="eq-stat-label">Overdue</span></div>
        <div className="eq-stat duesoon"><span className="eq-stat-value">{counts.dueSoon}</span><span className="eq-stat-label">Due Soon</span></div>
      </div>

      {/* Controls */}
      <div className="eq-controls">
        <input type="text" placeholder="Search equipment..." value={search} onChange={e => setSearch(e.target.value)} className="eq-search" />
        <div className="eq-filter-group">
          {['All', ...EQUIPMENT_TYPES].map(t => (
            <button key={t} className={`eq-filter-btn ${typeFilter === t ? 'active' : ''}`} onClick={() => setTypeFilter(t)}>
              {t.toUpperCase()}
            </button>
          ))}
        </div>
        {user.role === 'admin' && (
          <button className="eq-add-btn" onClick={() => { setForm(EMPTY_FORM); setSelected(null); setView('add'); }}>+ ADD EQUIPMENT</button>
        )}
      </div>

      <div className="eq-count">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</div>

      {equipment.length === 0 ? (
        <div className="eq-empty">
          <div className="eq-empty-icon">⚙️</div>
          <p>No equipment added yet.</p>
          {user.role === 'admin' && <button className="eq-btn primary" onClick={() => { setForm(EMPTY_FORM); setView('add'); }}>Add your first item</button>}
        </div>
      ) : filtered.length === 0 ? (
        <div className="eq-empty"><p>No equipment matches your filters.</p></div>
      ) : (
        <div className="eq-list">
          {filtered.map(eq => {
            const eqInsp = equipmentInspections(eq.id);
            const hasOverdue = eqInsp.some(i => i.next_due_date && daysUntil(i.next_due_date) < 0);
            const hasDueSoon = eqInsp.some(i => i.next_due_date && daysUntil(i.next_due_date) >= 0 && daysUntil(i.next_due_date) <= 30);
            const status = STATUS_CONFIG[eq.status] || STATUS_CONFIG['active'];
            const lolerInsp = latestInspection(eq.id, 'LOLER');
            const motInsp = latestInspection(eq.id, 'MOT');
            return (
              <div key={eq.id} className={`eq-card ${hasOverdue ? 'overdue' : hasDueSoon ? 'due-soon' : ''}`} onClick={() => { setSelected(eq); setView('detail'); }}>
                <div className="eq-card-left">
                  <div className="eq-card-name">{eq.name}</div>
                  <div className="eq-card-meta">
                    <span className="eq-type-badge">{eq.type}</span>
                    {eq.make && <span>{eq.make} {eq.model}</span>}
                    {eq.registration && <span className="eq-reg-badge">{eq.registration}</span>}
                    {eq.serial_number && <span className="eq-serial">S/N: {eq.serial_number}</span>}
                  </div>
                  <div className="eq-card-due">
                    {eq.requires_loler && <DueBadge date={lolerInsp?.next_due_date} label="LOLER" />}
                    {eq.mot_expiry && <DueBadge date={eq.mot_expiry} label="MOT" />}
                    {eq.tax_expiry && <DueBadge date={eq.tax_expiry} label="Tax" />}
                  </div>
                </div>
                <div className="eq-card-right">
                  <span className={`eq-status-badge ${status.cls}`}>{status.label}</span>
                  {eq.assigned_to && <span className="eq-assigned">{userName(eq.assigned_to)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
